import { Box, HStack, Link, Text, useColorModeValue, IconButton, useColorMode, VStack, Icon, Divider, Tooltip, Flex } from '@chakra-ui/react'
import { useEffect, useState, useCallback, useRef } from 'react'
import {
  FaChevronLeft, FaChevronRight, FaQrcode, FaSearch, FaChartBar,
  FaTools, FaCreditCard, FaKey, FaBell, FaList, FaEnvelope,
  FaShieldAlt, FaFileContract, FaUser, FaSignOutAlt, FaSignInAlt,
  FaBars, FaBoxes
} from '../utils/icons'
import { useNavigation } from '../contexts/navigation'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import { useWorkspace } from '../contexts/workspace'
import { MoonIcon, SunIcon } from '@chakra-ui/icons'

interface NavLinkProps {
  to: string;
  label: string;
  icon: React.ReactElement;
  isOpen: boolean;
  isOpening: boolean;
  isActive: boolean;
  color: string;
  hoverBg: string;
  activeBg: string;
  onClick?: (e: React.MouseEvent) => void;
}

const NavLink = ({ to, label, icon, isOpen, isOpening, isActive, color, hoverBg, activeBg, onClick }: NavLinkProps) => (
  <Tooltip 
    label={label} 
    isDisabled={isOpen} 
    placement="right" 
    hasArrow
    openDelay={0}
    closeDelay={0}
    closeOnClick
    portalProps={{ appendToParentPortal: false }}
  >
   <Link
      as={RouterLink}
      to={to}
      position="relative"
      color={isActive ? 'brand.500' : color}
      fontWeight="medium"
      px={4}
      py={2.5}
      w="full"
      borderRadius="md"
      bg={isActive ? activeBg : 'transparent'}
      _hover={{
        textDecoration: 'none',
        color: 'brand.500',
        bg: hoverBg,
      }}
      transition="all 0.3s ease-in-out"
      onClick={onClick}
    >
      <Box w="full" position="relative" overflow="hidden">
        <HStack
          spacing={3}
          w="full"
          transform={{"md": isOpen ? "translateX(0)" : "translateX(92.75%)"}}
          style={{
            transformOrigin: "right",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden"
          }}
          transition="transform 0.3s ease-in-out"
        >
            <Box
              style={{
                willChange: "transform",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden"
              }}
            >
              {icon}
            </Box>
          <Text
            opacity={(isOpen || isOpening) ? 1 : 0}
            maxWidth={(isOpen || isOpening) ? "200px" : "0"}
            overflow="hidden"
            whiteSpace="nowrap"
            transition="all 0.3s ease-in-out"
            style={{
              willChange: "opacity, max-width",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden"
            }}
          >
            {label}
          </Text>
        </HStack>
      </Box>
    </Link>
  </Tooltip>
)

interface NavigationProps {
  topmargin: {
    base: string;
    md: string;
  };
}

export default function Navigation({ topmargin }: NavigationProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuth() as { user: { name?: string; is_admin?: boolean; email?: string } | null; isAuthenticated: boolean; logout: () => Promise<void> }
  const { colorMode, toggleColorMode } = useColorMode()
  const { refreshWorkspaces } = useWorkspace() as { refreshWorkspaces: () => Promise<void> }

  useEffect(() => {
    if (isAuthenticated) {
      refreshWorkspaces();
    }
  }, [isAuthenticated, refreshWorkspaces]);

  const navBg = useColorModeValue('rgba(255, 255, 255, 1)', 'var(--chakra-colors-dark-card)')
  const buttonBorderColor = useColorModeValue('gray.200', 'gray.600')
  const linkColor = useColorModeValue('gray.600', 'gray.300')
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const activeBg = useColorModeValue('gray.100', 'whiteAlpha.200')
  const headerBg = useColorModeValue('white', 'gray.800')

  const appLinks = [
    { path: '/items', label: 'Inventory', icon: <FaBoxes /> },
    { path: '/search', label: 'Search Items', icon: <FaSearch /> },
    { path: '/scan', label: 'Scan Items', icon: <FaQrcode /> },
    //{ path: '/totes', label: 'Manage Totes', icon: <FaBox /> },
    { path: '/reports', label: 'Reports', icon: <FaChartBar /> },
    ...(user?.is_admin ? [{ path: '/admin', label: 'Admin Panel', icon: <FaTools /> }] : []),
  ]

  const { isNavOpen: isOpen = false, setIsNavOpen: setIsOpen, setDragTransform } = useNavigation() as { isNavOpen: boolean; setIsNavOpen: (isOpen: boolean) => void; setDragTransform: (transform: string | undefined) => void }
  const [ isOpening, setIsOpening ] = useState(false)
  const [dragStartX, setDragStartX] = useState<number | null>(null)
  const [currentDragX, setCurrentDragX] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isScrollLocked, setIsScrollLocked] = useState(false)

  useEffect(() => {
    // Function to lock/unlock body scrolling
    const lockBodyScroll = (lock: boolean) => {
      if (lock) {
        // Save current scroll position
        const scrollY = window.scrollY;
        // Add styles to prevent scrolling
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflowY = 'hidden';
      } else {
        // Restore scrolling
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        // Restore scroll position
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };

    // Only lock scrolling on mobile devices when menu is open
    if (window.innerWidth < 768) { // Typical mobile breakpoint
      setIsScrollLocked(isOpen || isDragging);
      lockBodyScroll(isOpen || isDragging);
    } else {
      setIsScrollLocked(false); // Never lock scrolling on desktop
      lockBodyScroll(false);
    }
    
    // Add resize listener to update scroll locking based on screen size
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsScrollLocked(isOpen || isDragging);
        lockBodyScroll(isOpen || isDragging);
      } else {
        setIsScrollLocked(false);
        lockBodyScroll(false);
      }
    }
    
    // Add a passive touch event listener to detect interactions outside the navigation
    const handleDocumentTouch = () => {
      // If we're not dragging, make sure we don't interfere with other touch interactions
      if (!isDragging && !touchHandledRef.current) {
        // Ensure we're not locking scrolling unnecessarily
        if (isScrollLocked && !isOpen) {
          setIsScrollLocked(false);
          lockBodyScroll(false);
        }
      }
    };
    
    document.addEventListener('touchstart', handleDocumentTouch, { passive: true });
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('touchstart', handleDocumentTouch);
      window.removeEventListener('resize', handleResize);
      // Always ensure scrolling is restored when component unmounts
      lockBodyScroll(false);
    };
  }, [isOpen, isDragging, isScrollLocked])

  const animationFrameRef = useRef<number>()
  const touchStartTimeRef = useRef<number>(0)
  const touchHandledRef = useRef<boolean>(false)
  const currentDragXRef = useRef<number | null>(null)
  const dragStartXRef = useRef<number | null>(null)
  const lastTransformRef = useRef<string | undefined>(undefined)
  
  // Debounce function to prevent too many state updates
  const debounce = useRef<NodeJS.Timeout | null>(null)
  
  // Force a repaint to ensure proper rendering
  const forceRepaint = useCallback(() => {
    // This forces a repaint by temporarily changing a property that affects layout
    const navElement = document.querySelector('nav');
    if (navElement) {
      navElement.style.display = 'none';
      // Force a reflow
      void navElement.offsetHeight;
      navElement.style.display = '';
    }
  }, []);
  
  // Helper function to safely update drag transform with debouncing
  const updateDragTransform = useCallback((transform: string | undefined) => {
    if (transform === lastTransformRef.current) return;
    lastTransformRef.current = transform;
    
    if (debounce.current) {
      clearTimeout(debounce.current);
    }
    
    debounce.current = setTimeout(() => {
      setDragTransform(transform);
      debounce.current = null;
    }, 5); // Small delay to batch updates
  }, [setDragTransform]);

  useEffect(() => {
    // Create refs to the menu buttons for direct access
    const openMenuButtonRef = document.querySelector('[aria-label="Open menu"]') as HTMLElement
    const closeMenuButtonRef = document.querySelector('[aria-label="Close menu"]') as HTMLElement
    
    if (!openMenuButtonRef || !closeMenuButtonRef) return
    
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      // Only handle touch events on the menu buttons
      if (!target.closest('[aria-label="Open menu"]') && !target.closest('[aria-label="Close menu"]')) {
        return
      }

      // Only prevent default on the menu buttons to avoid interfering with other touch interactions
      e.preventDefault()
      setIsOpening(true)
      touchHandledRef.current = true

      // Store values in both state and refs for better performance
      const clientX = e.touches[0].clientX
      touchStartTimeRef.current = Date.now()
      dragStartXRef.current = clientX
      setDragStartX(clientX)
      setIsDragging(true)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || !touchHandledRef.current) return

      // Only prevent default if we're actually handling the drag
      e.preventDefault()
      
      // Store the current touch position directly in a ref to avoid state updates
      const currentX = e.touches[0].clientX
      const startX = dragStartXRef.current !== null ? dragStartXRef.current : dragStartX!

      // Update the ref value
      currentDragXRef.current = currentX

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        // Calculate the new position based on the direction
        const newX = isOpen
          ? Math.min(startX, currentX)
          : Math.max(startX, Math.min(startX + 250, currentX))

        // Update position in both state and ref
        setCurrentDragX(newX)
        currentDragXRef.current = newX

        // Calculate and set transform immediately
        const dragDistance = newX - startX
        const contentTransform = isOpen
          ? `translateX(${Math.max(0, 250 + dragDistance)}px)`
          : `translateX(${Math.min(250, Math.max(0, dragDistance))}px)`

        // Use the debounced update function
        updateDragTransform(contentTransform)
      })
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isDragging || !touchHandledRef.current) return

      // Only prevent default if we're actually handling the drag
      if (e.target && (
        (e.target as HTMLElement).closest('[aria-label="Open menu"]') ||
        (e.target as HTMLElement).closest('[aria-label="Close menu"]')
      )) {
        e.preventDefault()
      }
      
      setIsOpening(false)
      touchHandledRef.current = false

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      const touchDuration = Date.now() - touchStartTimeRef.current
      if (touchDuration < 100) {
        const newState = !isOpen
        setIsOpen(newState)
        // Force repaint after animation completes for quick taps
        setTimeout(forceRepaint, 350)
        
        setIsDragging(false)
        setDragStartX(null)
        setCurrentDragX(null)
        dragStartXRef.current = null
        currentDragXRef.current = null
        updateDragTransform(undefined)
        return
      }

      // Use a cleanup function to ensure we don't have multiple animation frames
      const cleanupAndFinishDrag = () => {
        // Use refs for more reliable values
        const startX = dragStartXRef.current !== null ? dragStartXRef.current : dragStartX
        const currentX = currentDragXRef.current !== null ? currentDragXRef.current : currentDragX
        
        if (startX === null || currentX === null) {
          setIsDragging(false)
          setDragStartX(null)
          setCurrentDragX(null)
          dragStartXRef.current = null
          currentDragXRef.current = null
          updateDragTransform(undefined)
          return
        }

        const dragDistance = currentX - startX
        
        if (!isOpen && dragDistance > 50) {
          setIsOpen(true)
          // Force repaint after animation completes when opening
          setTimeout(forceRepaint, 350)
        }
        else if (isOpen && dragDistance < 50) {
          setIsOpen(false)
          // Force repaint after animation completes when closing
          setTimeout(forceRepaint, 350)
        }

        setIsDragging(false)
        setDragStartX(null)
        setCurrentDragX(null)
        dragStartXRef.current = null
        currentDragXRef.current = null
        updateDragTransform(undefined)
      }

      requestAnimationFrame(cleanupAndFinishDrag)
    }

    const handleTouchCancel = (e: TouchEvent) => {
      if (!touchHandledRef.current) return

      // Only prevent default if we're actually handling the drag
      if (e.target && (
        (e.target as HTMLElement).closest('[aria-label="Open menu"]') ||
        (e.target as HTMLElement).closest('[aria-label="Close menu"]')
      )) {
        e.preventDefault()
      }
      
      touchHandledRef.current = false

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      // Clean up all state and refs
      setIsDragging(false)
      setDragStartX(null)
      setCurrentDragX(null)
      dragStartXRef.current = null
      currentDragXRef.current = null
      updateDragTransform(undefined)
    }

    // Attach event listeners only to the menu buttons instead of the entire document
    openMenuButtonRef.addEventListener('touchstart', handleTouchStart, { passive: false })
    closeMenuButtonRef.addEventListener('touchstart', handleTouchStart, { passive: false })
    
    // These still need to be on document to track finger movement outside the buttons
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: false })
    document.addEventListener('touchcancel', handleTouchCancel, { passive: false })

    return () => {
      openMenuButtonRef.removeEventListener('touchstart', handleTouchStart)
      closeMenuButtonRef.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchCancel)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isDragging, dragStartX, currentDragX, isOpen, setIsOpen, setDragTransform, forceRepaint])



  // Update the toggle function to force a repaint when animation finishes
  const toggleNav = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    
    // Force a repaint after animation completes for both opening and closing
    setTimeout(forceRepaint, 350); // Slightly longer than the transition time (0.3s)
  }, [isOpen, setIsOpen, forceRepaint]);

  const getDragTransform = useCallback(() => {
    // Use refs for more reliable values, falling back to state if needed
    const startX = dragStartXRef.current !== null ? dragStartXRef.current : dragStartX
    const currentX = currentDragXRef.current !== null ? currentDragXRef.current : currentDragX
    
    if (!isDragging || startX === null || currentX === null) {
      return isOpen ? 'translateX(0)' : 'translateX(-100%)'
    }

    const dragDistance = currentX - startX
    const dragPercentage = (dragDistance / 250) * 100
    const basePosition = isOpen ? 0 : -100
    const normalizedDistance = Math.max(-100, Math.min(0, basePosition + dragPercentage))

    return `translateX(${normalizedDistance}%)`
  }, [isDragging, dragStartX, currentDragX, isOpen])

  const userLinks = [
    { to: '/billing', label: 'Billing', icon: <FaCreditCard /> },
    { to: '/change-password', label: 'Change Password', icon: <FaKey /> },
    { to: '/notifications', label: 'Notification Settings', icon: <FaBell /> },
    { to: '/plans', label: 'Plans', icon: <FaList /> },
    { to: '/contact', label: 'Contact Us', icon: <FaEnvelope /> },
    { to: '/privacy', label: 'Privacy Policy', icon: <FaShieldAlt /> },
    { to: '/terms', label: 'Terms of Service', icon: <FaFileContract /> },
  ]

  const unauthLinks = [
    { to: '/login', label: 'Log In', icon: <FaSignInAlt /> },
    { to: '/plans', label: 'Plans', icon: <FaList /> },
    { to: '/contact', label: 'Contact Us', icon: <FaEnvelope /> },
    { to: '/privacy', label: 'Privacy Policy', icon: <FaShieldAlt /> },
    { to: '/terms', label: 'Terms', icon: <FaFileContract /> },
  ]

  return (
    <>
      <Box
        as="nav"
        position="fixed"
        left={0}
        top={0}
        zIndex={2}
        h={{ md: `calc(100vh - ${topmargin.md})`, base: `calc(100vh - ${topmargin.base})` }}
        w="250px"
        backdropFilter="blur(12px)"
        bg={navBg}
        borderRight="1px"
        borderColor={buttonBorderColor}
        transform={{"md": (isOpen || isOpening)  ? 'translateX(0px)' : 'translateX(-200px)',
                    "base": (isOpen || isOpening) ? 'translateX(0px)' : 'translateX(-250px)'}}
        style={{
          transform: isDragging ? getDragTransform() : undefined,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          perspective: 1000,
          WebkitPerspective: 1000
        }}
        transition={isDragging ? 'none' : 'transform 0.3s ease-in-out'}
  sx={{
    touchAction: 'none', // Needed for proper drag handling
    ...(isScrollLocked && {
      '& + *': {
        // Prevent scrolling when menu is open or dragging
        overflow: isOpen || isDragging ? 'hidden' : 'auto',
        touchAction: isOpen || isDragging ? 'none' : 'pan-y'
      }
    })
  }}
      >
         <Box
          position="fixed"
          right={0}
          top="50%"
          transform="translateY(-50%) translateX(100%)"
          alignItems="center"
          marginTop="3px"
        >
          <Box
            position="relative"
            display={{ base: "flex", md: "none" }}
            alignItems="center"
          >
            <Box
              as="button"
              height="100px"
              width="24px"
              onClick={!isDragging ? toggleNav : undefined}
              borderRadius="0 4px 4px 0"
              bg={useColorModeValue('white', 'gray.800')}
              color="gray.500"
              _hover={{ bg: useColorModeValue('gray.50', 'gray.700'), color: useColorModeValue('gray.700', 'white') }}
              _active={{ bg: useColorModeValue('gray.100', 'gray.600') }}
              boxShadow="sm"
              border="1px solid"
              borderColor={buttonBorderColor}
              borderLeft="none"
              visibility={(isOpen || isOpening) ? 'hidden' : 'visible'}
              zIndex={2}
              transition="all 0.3s ease-in-out"
              alignItems="center"
              justifyContent="center"
              aria-label="Open menu"
              sx={{ touchAction: 'manipulation' }} // Better touch handling
            >
              <Box
                position="relative"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="space-between"
                py={2}
                h="full"
              >
                <FaChevronRight size="10px" />
                <Box
                  sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontSize: '11px',
                    fontWeight: 'medium',
                    letterSpacing: '0.5px'
                  }}
                >
                  MENU
                </Box>
                <FaBars size="12px" />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          position="fixed"
          right={0}
          top="50%"
          transform="translateY(-50%)"
          alignItems="center"
          marginTop="3px"
        >
          <Box
            position="relative"
            display={{ base: "flex", md: "none" }}
            alignItems="center"
          >
            <Box
              as="button"
              height="100px"
              width="24px"
              onClick={!isDragging ? toggleNav : undefined}
              transform="translateX(100%)"
              borderRadius="0 4px 4px 0"
              bg={useColorModeValue('white', 'gray.800')}
              color="gray.500"
              _hover={{ bg: useColorModeValue('gray.50', 'gray.700'), color: useColorModeValue('gray.700', 'white') }}
              _active={{ bg: useColorModeValue('gray.100', 'gray.600') }}
              boxShadow="sm"
              border="1px solid"
              borderColor={buttonBorderColor}
              borderLeft="none"
              visibility={(isOpen || isOpening) ? 'visible' : 'hidden'}
              zIndex={2}
              transition="all 0.3s ease-in-out"
              alignItems="center"
              justifyContent="center"
              aria-label="Close menu"
              sx={{ touchAction: 'manipulation' }} // Better touch handling
            >
              <Box
                position="relative"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="space-between"
                py={2}
                h="full"
              >
                <FaChevronLeft size="10px" />
                <Box
                  sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontSize: '11px',
                    fontWeight: 'medium',
                    letterSpacing: '0.5px'
                  }}
                >
                  MENU
                </Box>
                <FaBars size="12px" />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box h="full" display="flex" flexDirection="column">
          <Box 
            py={4} 
            px={4} 
            borderBottom="1px" 
            borderColor={buttonBorderColor}
            bg={headerBg}
            position="relative"
            overflow="hidden"
          >
            <Link
              as={RouterLink}
              to="/"
              color="brand.500"
              fontWeight="bold"
              fontSize="xl"
              display="block"
              textAlign="center"
              _hover={{
                textDecoration: 'none',
                transform: 'translateY(-1px)',
              }}
              transition="all 0.3s ease-in-out"
            >
              <Text
                position="relative"
                transition="transform 0.3s ease-in-out"
                transform={{"md": isOpen ? "translateX(0)" : "translateX(137px)","base": ""}}
                display="inline-block"
                style={{
                  willChange: "transform",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden"
                }}
              >I</Text>
              <Text
                position="relative"
                opacity={(isOpen || isOpening) ? 1 : 0}
                transform={{"md": isOpen ? "translateX(0)" : "translateX(137px)","base": ""}}
                transition="transform 0.3s ease-in-out, opacity 0.3s ease-in-out"
                display="inline-block"
                style={{
                  willChange: "transform, opacity",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden"
                }}
              >temizer</Text>
            </Link>
          </Box>

          {isAuthenticated ? (
            <VStack h="full" align="stretch" spacing={4} overflowX="hidden" overflowY="auto">
              <VStack py={4} spacing={6}>
                <VStack align="stretch" spacing={1} w="full">
                  {appLinks.map(({ path, label, icon }) => (
                    <NavLink
                      key={path}
                      to={path}
                      label={label}
                      icon={icon}
                      isOpen={isOpen}
                      isOpening={isOpening}
                      isActive={!!location.pathname.match(new RegExp(`^${path}(/.*)?$`))}
                      color={linkColor}
                      hoverBg={hoverBg}
                      activeBg={activeBg}
                    />
                  ))}
                </VStack>
              </VStack>
              <Flex direction="column" h="full" justifyContent="flex-end" w="full">
                <HStack w="full" justify="space-between" px={4} py={2} borderBottom ="1px" borderColor={buttonBorderColor}>
                  <HStack justify="flex-start">
                    <Icon as={FaUser} /><Divider />
                    <Text fontWeight="bold" fontSize="sm">{user?.name}</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Tooltip 
                      label="Toggle color mode" 
                      isDisabled={isOpen} 
                      placement="right"
                      hasArrow
                      shouldWrapChildren
                      openDelay={0}
                      closeDelay={0}
                      closeOnClick
                      portalProps={{ appendToParentPortal: false }}
                    >
                      <IconButton
                        aria-label="Toggle color mode"
                        icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={toggleColorMode}
                        _hover={{ bg: hoverBg }}
                      />
                    </Tooltip>
                    <Tooltip 
                      label={"Open menu"} 
                      isDisabled={isOpen} 
                      placement="right"
                      hasArrow
                      shouldWrapChildren
                      openDelay={0}
                      closeDelay={0}
                      closeOnClick
                      portalProps={{ appendToParentPortal: false }}
                    >
                      <IconButton
                        display={{ base: "none", md: "flex" }}
                        pl={4}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                        icon={isOpen ? <FaChevronLeft /> : <FaChevronRight />}
                        variant="ghost"
                        size="sm"
                        onClick={toggleNav}
                        _hover={{ bg: hoverBg }}
                      />
                    </Tooltip>
                  </HStack>
                </HStack>
                {userLinks.map(({ to, label, icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    label={label}
                    icon={icon}
                    isOpen={isOpen}
                    isOpening={isOpening}
                    isActive={location.pathname === to}
                    color={linkColor}
                    hoverBg={hoverBg}
                    activeBg={activeBg}
                  />
                ))}
                <NavLink
                  to="/login"
                  label="Logout"
                  icon={<FaSignOutAlt />}
                  isOpen={isOpen}
                  isOpening={isOpening}
                  isActive={false}
                  color={linkColor}
                  hoverBg={hoverBg}
                  activeBg={activeBg}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    logout();
                    navigate('/login', { replace: true });
                  }}
                />
              </Flex>
            </VStack>
          ) : (
            <VStack h="full" align="stretch" spacing={4} overflowX="hidden" overflowY="auto">
              <VStack py={4} spacing={6}>
                <VStack align="stretch" spacing={1} w="full">
                  {unauthLinks.map(({ to, label, icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      label={label}
                      icon={icon}
                      isOpen={isOpen}
                      isOpening={isOpening}
                      isActive={location.pathname === to}
                      color={linkColor}
                      hoverBg={hoverBg}
                      activeBg={activeBg}
                    />
                  ))}
                </VStack>

                <Box mt="auto" pt={4} w="full" borderTop="1px" borderColor={buttonBorderColor}>
                  <HStack px={4} py={2} justify="space-between">
                    <Tooltip 
                      label="Toggle color mode" 
                      isDisabled={isOpen} 
                      placement="right"
                      hasArrow
                      shouldWrapChildren
                      openDelay={0}
                      closeDelay={0}
                      closeOnClick
                      portalProps={{ appendToParentPortal: false }}
                    >
                      <IconButton
                        aria-label="Toggle color mode"
                        icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={toggleColorMode}
                        _hover={{ bg: hoverBg }}
                      />
                    </Tooltip>
                    <Tooltip 
                      label={isOpen ? "Close menu" : "Open menu"} 
                      isDisabled={isOpen} 
                      placement="right"
                      hasArrow
                      shouldWrapChildren
                      openDelay={0}
                      closeDelay={0}
                      closeOnClick
                      portalProps={{ appendToParentPortal: false }}
                    >
                      <IconButton
                        display={{ base: "none", md: "flex" }}
                        pl={4}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                        icon={isOpen ? <FaChevronLeft /> : <FaChevronRight />}
                        variant="ghost"
                        size="sm"
                        onClick={toggleNav}
                        _hover={{ bg: hoverBg }}
                      />
                    </Tooltip>
                  </HStack>
                </Box>
              </VStack>
            </VStack>
          )}
        </Box>
      </Box>
    </>
  );
}
